export type OrganizationType = 
  | 'FARMER_ORGANIZATION'
  | 'INPUT_PROVIDER'
  | 'OFFTAKER_PROCESSOR'
  | 'FINANCIAL_INSTITUTION'
  | 'GOVERNMENT_ENTITY'
  | 'INTERNATIONAL_ORGANIZATION'
  | 'BROKER'
  | 'CALCULATING_AGENT'
  | 'INSURANCE_COMPANY'
  | 'REINSURANCE_COMPANY'
  | 'OTHER';

export type AffiliatedUser = {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
};

export type AssociatedFarmer = {
  id: string;
  name: string;
  phone: string;
  isActive: boolean;
};

export interface Organization {
  id: string;
  name: string;
  code: string;
  type: OrganizationType;
  settings?: OrganizationSettings;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  logoUrl?: string;
  logoFilename?: string;
  logoMimeType?: string;
  logoFileSize?: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  updatedBy?: string;
  userCount?: number;
  users?: AffiliatedUser[];
  farmers?: AssociatedFarmer[];
}

// Simple contact person type matching the existing v1.1.0 schema
export interface ContactPerson {
  name: string;
  role: string;
  contactNumber?: string;
  email?: string;
}

// Simple organization settings matching v1.1.0 schema
export interface OrganizationSettings {
  email?: string;
  tin?: string;
  contact_person?: ContactPerson;
  [key: string]: unknown; // Allow other settings as defined in schema
}

export interface CreateOrganizationData {
  name: string;
  code: string;
  type: OrganizationType;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  settings?: OrganizationSettings;
}

export interface UpdateOrganizationData {
  name?: string;
  type?: OrganizationType;  // Issue #108: Allow updating organization type
  isActive?: boolean;       // Issue #108: Allow updating organization status
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  settings?: OrganizationSettings;
}

export interface OrganizationFilters {
  search?: string;
  type?: OrganizationType;
  isActive?: boolean;
}

export interface PaginatedOrganizationResponse {
  organizations: Organization[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface OrganizationTableColumn {
  key: keyof Organization | 'actions';
  label: string;
  sortable?: boolean;
  searchable?: boolean;
  render?: (organization: Organization) => React.ReactNode;
}

export interface OrganizationTableProps {
  organizations: Organization[];
  loading?: boolean;
  onEdit?: (organization: Organization) => void;
  onDelete?: (organization: Organization) => void;
  onUploadLogo?: (organization: Organization) => void;
  filters?: OrganizationFilters;
  onFiltersChange?: (filters: OrganizationFilters) => void;
}

export const ORGANIZATION_TYPE_LABELS: Record<OrganizationType, string> = {
  FARMER_ORGANIZATION: 'Farmer Organization',
  INPUT_PROVIDER: 'Input Provider',
  OFFTAKER_PROCESSOR: 'Offtaker/Processor',
  FINANCIAL_INSTITUTION: 'Financial Institution',
  GOVERNMENT_ENTITY: 'Government Entity',
  INTERNATIONAL_ORGANIZATION: 'International Organization',
  BROKER: 'Broker',
  CALCULATING_AGENT: 'Calculating Agent',
  INSURANCE_COMPANY: 'Insurance Company',
  REINSURANCE_COMPANY: 'Reinsurance Company',
  OTHER: 'Other',
}; 